import useTeamComparison from '@amf/shared/data/hooks/useTeamComparison';
import classNames from 'classnames';
import React, { ReactNode } from 'react';
import { useTranslate } from '@tolgee/react';
import Loader from '@amf/shared/components/utils/Loader';
import Link from 'next/link';

interface TeamComparisonProgressProps {
  value: number;
  max: number;
  align: 'left' | 'right';
}

function TeamComparisonProgress({ max, value, align }: TeamComparisonProgressProps) {
  const width = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className={classNames('TeamComparison__progress', `TeamComparison__progress--${align}`)}>
      <div className='TeamComparison__progressThumb' style={{ width: `${width}%` }} />
    </div>
  );
}

interface TeamComparisonItemProps {
  title: string;
  left: { title: ReactNode; value: number };
  right: { title: ReactNode; value: number };
}

function TeamComparisonItem({ left, right, title }: TeamComparisonItemProps) {
  const max = Math.max(left.value, right.value);
  return (
    <div className='TeamComparison__item'>
      <div className='TeamComparison__value TeamComparison__value--left'>{left.title}</div>
      <TeamComparisonProgress max={max} value={left.value} align='right' />
      <div className='TeamComparison__itemTitle'>{title}</div>
      <TeamComparisonProgress max={max} value={right.value} align='left' />
      <div className='TeamComparison__value TeamComparison__value--right'>{right.title}</div>
    </div>
  );
}

interface TeamComparisonProps {
  leagueId: string;
  firstTeamId: string;
  secondTeamId: string;
  title?: string;
  center?: boolean;
  prefixes: { team: string };
}

function TeamComparison({
  leagueId,
  firstTeamId,
  secondTeamId,
  title,
  center,
  prefixes,
}: TeamComparisonProps) {
  const { t } = useTranslate();
  const { comparison, loading } = useTeamComparison(leagueId, firstTeamId, secondTeamId);

  if (!loading && !comparison) return null;
  return (
    <div className='TeamComparison'>
      {title && (
        <h2
          className={classNames('TeamComparison__title', {
            'TeamComparison__title--center': center,
          })}
        >
          {title}
        </h2>
      )}
      {loading && <Loader size='small'>{t('loading')}</Loader>}
      {!loading && comparison && (
        <div className='TeamComparison__content'>
          <div className='TeamComparison__header'>
            <div />
            <Link href={`${prefixes.team}/${comparison.a.team.id}`}>
              <a className='TeamComparison__headerTeam TeamComparison__headerTeam--left'>
                {comparison.a.team.name}
              </a>
            </Link>
            <div />
            <Link href={`${prefixes.team}/${comparison.b.team.id}`}>
              <a className='TeamComparison__headerTeam TeamComparison__headerTeam--right'>
                {comparison.b.team.name}
              </a>
            </Link>
            <div />
          </div>
          <TeamComparisonItem
            title={t('team.comparison.age')}
            left={{ value: comparison.a.age, title: comparison.a.age }}
            right={{ value: comparison.b.age, title: comparison.b.age }}
          />
          <TeamComparisonItem
            title={t('team.comparison.matches')}
            left={{ value: comparison.a.completed, title: comparison.a.completed }}
            right={{ value: comparison.b.completed, title: comparison.b.completed }}
          />
          <TeamComparisonItem
            title={t('team.comparison.wins')}
            left={{ value: comparison.a.wins, title: comparison.a.wins }}
            right={{ value: comparison.b.wins, title: comparison.b.wins }}
          />
          <TeamComparisonItem
            title={t('team.comparison.winsInExtension')}
            left={{ value: comparison.a.winsInExtension, title: comparison.a.winsInExtension }}
            right={{ value: comparison.b.winsInExtension, title: comparison.b.winsInExtension }}
          />
          <TeamComparisonItem
            title={t('team.comparison.draws')}
            left={{ value: comparison.a.draws, title: comparison.a.draws }}
            right={{ value: comparison.b.draws, title: comparison.b.draws }}
          />
          <TeamComparisonItem
            title={t('team.comparison.lost')}
            left={{ value: comparison.a.lost, title: comparison.a.lost }}
            right={{ value: comparison.b.lost, title: comparison.b.lost }}
          />
          <TeamComparisonItem
            title={t('team.comparison.goals')}
            left={{ value: comparison.a.goals, title: comparison.a.goals }}
            right={{ value: comparison.b.goals, title: comparison.b.goals }}
          />
          <TeamComparisonItem
            title={t('team.comparison.receivedGoals')}
            left={{ value: comparison.a.receivedGoals, title: comparison.a.receivedGoals }}
            right={{ value: comparison.b.receivedGoals, title: comparison.b.receivedGoals }}
          />
          <TeamComparisonItem
            title={t('team.comparison.yellowCards')}
            left={{ value: comparison.a.yellowCards, title: comparison.a.yellowCards }}
            right={{ value: comparison.b.yellowCards, title: comparison.b.yellowCards }}
          />
          <TeamComparisonItem
            title={t('team.comparison.redCards')}
            left={{ value: comparison.a.redCards, title: comparison.a.redCards }}
            right={{ value: comparison.b.redCards, title: comparison.b.redCards }}
          />
        </div>
      )}
    </div>
  );
}

export default TeamComparison;
